'use client'
import { useEffect, useState } from 'react'
import { useCart } from '@/context/CartContext'
import { calcPreview } from '@/lib/pricing'
import { IconPlus, IconMinus, IconCheck } from '@/icons'

export default function ProductModal({ product, onClose }) {
  const { add, items, openCart } = useCart()
  const [added, setAdded] = useState({})
  const [qtys, setQtys]   = useState({})

  useEffect(() => {
    if (!product) return
    const onKey = (e) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onKey)
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = ''
    }
  }, [product, onClose])

  useEffect(() => { setAdded({}); setQtys({}) }, [product?.title])

  if (!product) return null

  const rowId   = (item, row) => `${product.title}|${item.name}|${row.label}`
  const rowQty  = (id) => Math.max(1, qtys[id] ?? 1)
  const cartQty = (id) => items.find(x => x.id === id)?.qty ?? 0

  const changeQty = (id, val) => {
    const n = parseInt(val)
    setQtys(prev => ({ ...prev, [id]: isNaN(n) || n < 1 ? 1 : n }))
  }

  const handleAdd = (item, row) => {
    const id  = rowId(item, row)
    const qty = rowQty(id)
    add({
      id,
      category:       product.title,
      name:           item.name,
      label:          row.label,
      retail:         row.retail,
      price:          row.price          ?? null,
      wholesalePrice: row.wholesalePrice ?? null,
      wholesaleUnit:  row.wholesaleUnit  ?? null,
    }, qty)
    setAdded(prev => ({ ...prev, [id]: true }))
    setTimeout(() => setAdded(prev => ({ ...prev, [id]: false })), 1500)
  }

  return (
    <div className="modal-backdrop" onMouseDown={onClose}>
      <div className="modal-card" onMouseDown={e => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="product-modal-title">

        <div className="modal-hd">
          <div>
            <h2 className="modal-title" id="product-modal-title">{product.title}</h2>
            <p className="modal-desc">{product.desc}</p>
          </div>
          <button className="modal-close" onClick={onClose} aria-label="Закрыть">✕</button>
        </div>

        <div className="modal-body">
          <div className="modal-items">
            {product.items.map((item, i) => (
              <div className="modal-item" key={i}>
                <div className="modal-item-hd">
                  <span className="modal-item-name">{item.name}</span>
                  {item.note && <span className="modal-item-note">{item.note}</span>}
                </div>

                {/* Box wholesale notice */}
                {item.rows.some(r => r.wholesaleUnit) && (() => {
                  const r = item.rows.find(r => r.wholesaleUnit)
                  return (
                    <div className="modal-wholesale-notice">
                      Поставляется коробками по <strong>{r.wholesaleUnit} шт.</strong>
                      &nbsp;·&nbsp;
                      Полная коробка: <strong>{r.wholesale}/шт.</strong>
                      &nbsp;·&nbsp;
                      Менее {r.wholesaleUnit} шт.: {r.retail}/шт.
                    </div>
                  )
                })()}

                <ul className="modal-item-rows">
                  {item.rows.map((row, j) => {
                    const id        = rowId(item, row)
                    const qty       = rowQty(id)
                    const inCart    = cartQty(id)
                    const justAdded = added[id]
                    const preview   = calcPreview(row, qty)

                    return (
                      <li key={j}>
                        {/* label */}
                        <span className="modal-row-label">{row.label}</span>

                        {/* unit prices */}
                        <span className="modal-row-prices">
                          <span className="modal-row-retail">{row.retail}</span>
                          {row.wholesale && (
                            <>
                              <span className="modal-row-sep" />
                              <span className="modal-row-wholesale">
                                {row.wholesale} <span className="modal-row-opt">опт/кор.</span>
                              </span>
                            </>
                          )}
                        </span>

                        {/* qty stepper + live price preview */}
                        {row.price !== null && (
                          <div className="row-qty-wrap">
                            <div className="row-qty-ctrl">
                              <button onClick={() => changeQty(id, qty - 1)} disabled={qty <= 1} aria-label="Меньше">
                                <IconMinus width="11" height="11" />
                              </button>
                              <input
                                type="number" min="1" value={qty}
                                onChange={e => changeQty(id, e.target.value)}
                                className="row-qty-input"
                                aria-label="Количество"
                              />
                              <button onClick={() => changeQty(id, qty + 1)} aria-label="Больше">
                                <IconPlus width="11" height="11" />
                              </button>
                            </div>
                            {preview && <span className="row-price-preview">{preview}</span>}
                          </div>
                        )}

                        {/* add button */}
                        <button
                          className={`add-to-cart-btn ${justAdded ? 'added' : ''}`}
                          onClick={() => handleAdd(item, row)}
                        >
                          {justAdded
                            ? <><IconCheck width="13" height="13" /> Добавлено</>
                            : <>В корзину{inCart > 0 && <span className="add-qty-badge">{inCart}</span>}</>
                          }
                        </button>
                      </li>
                    )
                  })}
                </ul>
              </div>
            ))}
          </div>

          <button
            className="btn btn-ghost"
            style={{ marginTop: 20, width: '100%', justifyContent: 'center' }}
            onClick={() => { onClose(); openCart() }}
          >
            Перейти в корзину
          </button>
        </div>
      </div>
    </div>
  )
}
